import { fetchInboxes, handleMarkStatusReport } from '@/entities/inbox';
import { isC1cStudy } from '@/entities/study';
import { EMITTER_CONSTANTS } from '@/shared/config';
import {
  auth,
  emitter,
  showMessageError,
  SocketIo,
  GETTEXT_ALL_FACILITIES,
  GETTEXT_ALL_PRIORITY,
  GETTEXT_ALL_STUDIES,
} from '@/shared/lib';
import { useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useImperativeHandle, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatSortValue } from '../../lib/inboxSearch';
import { inboxesFormatter } from '../../lib/inboxList';
import type { InboxListProps } from './inboxList.types';

/**
 * Drives one inbox list tab: cursor paging, filter and sort state, socket row updates and status-report saves for the tab's report type. Builds a filterObject ref from searchGoto and the tab type, fetches through fetchInboxes on activation and on facility or study-scope change, exposes handleReloadData and fetchData on the imperative ref, listens to the inbox, mark-inbox, mark-status-report and report-updated emitter events, and tracks isEndOfData against totalInboxCount.
 *
 * @param isActive - Whether this tab is the visible one.
 * @param type - The report type this tab lists, such as All or End of Use.
 * @param totalInboxCount - Total matching reports, used to detect the end of data.
 * @param searchGoto - Persisted search, sort and status filter values.
 * @param selectedFacilityProp - The facility option currently selected.
 * @param selectedTypeSeePatients - The all-studies or your-studies scope option.
 * @param ref - Forwarded ref receiving handleReloadData and fetchData.
 * @returns State, the filter and page-size refs, the fetcher and the row handlers the tab renders with.
 */
const useInboxList = (
  {
    isActive,
    type,
    totalInboxCount,
    searchGoto,
    selectedFacility: selectedFacilityProp,
    selectedTypeSeePatients,
  }: InboxListProps,
  ref: any,
) => {
  const navigate = useNavigate();

  const isAllReports = type === 'All';

  const isSearchApply = useRef(false);
  const joinedDeviceIdsRef = useRef<any>([]);

  /**
   * Subscribes the socket connection to the device rooms of the inbox reports just loaded so their report-updated events arrive. Collects each report's study.deviceId, drops empty and 'undefined' values, unions them with the ids already held in joinedDeviceIdsRef, and emits a `report-updated-{deviceId}` room only for the ids that are new.
   *
   * @param reports - The report list whose device ids should be joined.
   * @returns No return value.
   */
  const joinDeviceRooms = (reports: any) => {
    // Join device rooms (unique deviceIds only)
    const validDeviceIds = reports
      .map((report: any) => report.study?.deviceId)
      .filter((deviceId: any) => deviceId && deviceId !== 'undefined');
    const updatedDeviceIds = _.union(joinedDeviceIdsRef.current, validDeviceIds);
    const newDeviceIds = _.difference(updatedDeviceIds, joinedDeviceIdsRef.current);
    joinedDeviceIdsRef.current = updatedDeviceIds;
    _.forEach(newDeviceIds, (deviceId) => {
      const deviceRoomId = `report-updated-${deviceId}`;
      (SocketIo as any).emitRoom(deviceRoomId);
    });
  };

  const filterObject = useRef<any>({
    type: type === 'All' ? undefined : type === 'End of Use' ? 'Study' : type,
    priority: searchGoto.priority || GETTEXT_ALL_PRIORITY,
    patientName:
      searchGoto.searchByValue === 'patientName' ? _.trim(searchGoto.searchValue) : undefined,
    patientId:
      searchGoto.searchByValue === 'patientId' ? _.trim(searchGoto.searchValue) : undefined,
    physicianId:
      searchGoto.searchByValue === 'physicianId'
        ? searchGoto.physicianSearchValue?.value
        : undefined,
    studyFid:
      searchGoto.searchByValue === 'studyFid'
        ? searchGoto.searchValue?.toString()?.trim() &&
          !_.isNaN(parseInt(searchGoto.searchValue, 10))
          ? parseInt(searchGoto.searchValue, 10)
          : undefined
        : undefined,
    cursor: undefined,
    rankCursor: undefined,
    sortBy: formatSortValue(searchGoto.sortValue ?? '').sortBy,
    sortOrder: formatSortValue(searchGoto.sortValue ?? '').sortOrder,
    isRecentInbox: false,
    statusReports: isAllReports ? searchGoto.statusAllReports : searchGoto.statusReports,
  });
  const sizePerPage = useRef(10);
  const needFetchMoreInboxes = useRef(false);
  const needFetchDataWithNewFacility = useRef(false);
  const needFetchDataWithStatusReportChanged = useRef(false);
  const selectedFacility = useRef(_.cloneDeep(selectedFacilityProp));

  const [state, setState] = useSetState<any>({
    skeletonInboxes: _.map(_.range(10), () => ({})),
    inboxes: [],
    isLoadedData: false,
    pageIndex: 0,
    maxDisplayedInboxes: 10,
    isEndOfData: false,
    isShowFacilityName: selectedFacilityProp.value === GETTEXT_ALL_FACILITIES,
    listChangingStatusReport: [],
  });

  /**
   * Loads a page of the inbox list, either replacing the rows or appending the next cursor page. Merges updatedFilterObject into the filterObject ref, maps the all-priority sentinel to undefined, coerces studyFid to an integer, keeps rankCursor only when paging forward on a PRIORITY sort, joins the returned device rooms, then concatenates and advances pageIndex for a cursor query or resets to page zero; failures log, toast and empty the list.
   *
   * @param updatedFilterObject - Filter fields to merge into the persistent query filter.
   * @param isFetchMoreByClickingNext - Whether this load is the user paging forward.
   * @returns A promise settling once state reflects the loaded page or the failure.
   */
  const fetchData = async (updatedFilterObject = {}, isFetchMoreByClickingNext = false) => {
    filterObject.current.facilityId =
      selectedFacilityProp.value !== GETTEXT_ALL_FACILITIES
        ? selectedFacilityProp.value
        : undefined;

    const queryFilter = { ...filterObject.current, ...updatedFilterObject };
    filterObject.current = { ...queryFilter };
    if (queryFilter.priority === GETTEXT_ALL_PRIORITY) {
      queryFilter.priority = undefined;
    }
    try {
      const queryFilterPayload = {
        ...queryFilter,
        studyFid:
          queryFilter?.studyFid?.toString()?.trim() && !_.isNaN(parseInt(queryFilter?.studyFid, 10))
            ? parseInt(queryFilter?.studyFid, 10)
            : undefined,
        viewAll: selectedTypeSeePatients.value === GETTEXT_ALL_STUDIES,
      };
      if (isFetchMoreByClickingNext) {
        _.assign(queryFilterPayload, {
          rankCursor: queryFilter.sortBy === 'PRIORITY' ? queryFilter.rankCursor : undefined,
        });
      } else {
        _.assign(queryFilterPayload, {
          rankCursor: undefined,
        });
      }
      const { reports } = await fetchInboxes(queryFilterPayload);
      // *: Join device rooms
      joinDeviceRooms(reports);

      const newInboxes = inboxesFormatter(reports);

      const setStateObject = {};

      if (queryFilter.cursor) {
        const concatenatedInboxes = [...state.inboxes, ...newInboxes];
        _.assign(setStateObject, {
          inboxes: concatenatedInboxes,
          pageIndex:
            isFetchMoreByClickingNext && !!newInboxes.length
              ? state.pageIndex + 1
              : state.pageIndex,
          maxDisplayedInboxes:
            isFetchMoreByClickingNext && !!newInboxes.length
              ? state.maxDisplayedInboxes + 10
              : state.maxDisplayedInboxes,
          isEndOfData: newInboxes.length < 10 || concatenatedInboxes.length === totalInboxCount,
          isLoadedData: true,
        });
      } else {
        _.assign(setStateObject, {
          inboxes: newInboxes,
          pageIndex: 0,
          maxDisplayedInboxes: 10,
          isEndOfData: newInboxes.length < 10 || newInboxes.length === totalInboxCount,
          isLoadedData: true,
        });
      }

      setState(setStateObject);
    } catch (error) {
      console.error('Failed to fetch inboxes: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState({
        inboxes: [],
        isLoadedData: true,
      });
    }

    if (isFetchMoreByClickingNext) {
      needFetchMoreInboxes.current = false;
    }
  };

  /**
   * Applies filter changes arriving from the inbox search bar while this tab is active. Merges the incoming filter into filterObject, refetches with the current fetch-more flag and drops the list back into its loading state; an inactive tab ignores the change.
   *
   * @param updatedFilterObject - Filter fields the search bar changed.
   * @returns No return value.
   */
  const onChangeSearchComponent = (updatedFilterObject: any) => {
    if (isActive) {
      _.assign(filterObject.current, { ...updatedFilterObject });
      fetchData(filterObject.current, needFetchMoreInboxes.current);
      setState({ isLoadedData: false });
    }
  };

  /**
   * Moves the inbox list to another page, loading more rows first when the target page runs past what is cached. When the requested page would exceed state.inboxes and the end of data is not reached it sets cursor to the last row's date (and rankCursor to its priority on a PRIORITY sort) and refetches; otherwise it just stores the new pageIndex.
   *
   * @param newPageIndex - The zero-based page to display.
   * @returns No return value.
   */
  const navigatePage = (newPageIndex: any) => {
    if (newPageIndex * 10 + 10 >= state.inboxes.length && !state.isEndOfData) {
      needFetchMoreInboxes.current = true;

      filterObject.current.cursor = state.inboxes[state.inboxes.length - 1].date;
      filterObject.current.rankCursor =
        filterObject.current?.sortBy === 'PRIORITY'
          ? state.inboxes[state.inboxes.length - 1].priority
          : undefined;
      fetchData(filterObject.current, needFetchMoreInboxes.current);
      setState({ isLoadedData: false });
    } else {
      setState({ pageIndex: newPageIndex });
    }
  };

  /**
   * Reloads the inbox list from its first page. Clears isLoadedData and refetches with cursor and rankCursor cleared so cursor paging restarts.
   *
   * @returns No return value.
   */
  const handleReloadData = () => {
    setState({ isLoadedData: false });
    fetchData({ cursor: undefined, rankCursor: undefined }, false);
  };

  useImperativeHandle(ref, () => ({
    handleReloadData,
    fetchData,
  }));

  /**
   * Opens the report behind the inbox row the user clicked. A report of a CardioOne study raises the CardioOne report notice; any other report navigates to the `/inbox/{id}` route.
   *
   * @param clickedReport - The inbox row that was clicked.
   * @returns No return value.
   */
  const onClickReport = (clickedReport: any) => {
    if (isC1cStudy(clickedReport.study ?? {})) {
      emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_C1C_REPORT);
      return;
    }
    navigate(`/inbox/${clickedReport.id}`);
  };

  /**
   * Applies an inbox socket event to this inbox tab. A recalled message removes the matching row from a clone of state.inboxes; a matching row is merged with msg.inbox; an unknown id refetches from the first page only when the tab type accepts the message type (All, End of Use to Study, Notification to Event or Notification, or an exact match), the list sits on page zero, and the 'Your studies' scope does not exclude a clinic physician viewer.
   *
   * @param msg - The inbox socket event payload.
   * @returns No return value.
   */
  const handleUpdateInboxEvent = (msg: any) => {
    const cloneInboxes = _.cloneDeep(state.inboxes);
    if (msg.recalled) {
      _.remove(cloneInboxes, (x) => x.id === msg.id);
      setState({ inboxes: cloneInboxes });
    } else {
      /**
       * The cloned inbox entry whose id matches the incoming inbox event, or undefined when the report is not on the current page. Derived by scanning the deep-cloned inboxes array for an id equal to the message id.
       *
       * @returns The matching inbox row from the cloned list, or undefined.
       */
      const ele = _.find(cloneInboxes, (x) => x.id === msg.id);
      if (ele) {
        _.assign(ele, msg.inbox);
        setState({ inboxes: cloneInboxes });
      } else if (
        (type === 'All' || // *: All reports
          (type === 'End of Use' && msg.type === 'Study') || // *: End of Use reports
          (type === 'Notification' && (msg.type === 'Event' || msg.type === 'Notification')) || // *: Notification reports
          msg.type === type) && // *: Interim reports
        state.pageIndex === 0
      ) {
        if (selectedTypeSeePatients.value === 'Your studies') {
          if (auth.isClinicPhysician() && msg.physician !== auth.userId()) {
            return;
          }
        }
        fetchData({ cursor: undefined, rankCursor: undefined }, false);
      }
    }
  };

  /**
   * Applies a mark-inbox socket message to the matching inbox row. Finds the entry by id in a deep clone of state.inboxes, merges the message into it and commits the clone; a message for an id that is not listed changes nothing.
   *
   * @param msg - The mark-inbox socket message.
   * @returns No return value.
   */
  const handleMarkInboxEvent = (msg: any) => {
    const cloneInboxes = _.cloneDeep(state.inboxes);
    /**
     * The cloned inbox entry whose id matches the incoming mark event, or undefined when the report is not in the current page. Derived by scanning the deep-cloned inboxes array for an id equal to the message id.
     *
     * @returns The matching inbox row from the cloned list, or undefined.
     */
    const ele = _.find(cloneInboxes, (x) => x.id === msg.id);
    if (ele) {
      _.assign(ele, msg);
      setState({ inboxes: cloneInboxes });
    }
  };

  /**
   * Applies a report-updated socket message to the matching inbox row. Ignores messages without a status, matches on id plus an exact non-nil isNonPhi so the PHI and non-PHI variants stay distinct, tracks isRenderingBackground transitions, and clears the cached pdfArrayBuffer and url when the report just reached Ready or finished background rendering.
   *
   * @param msg - The report-updated socket message.
   * @returns No return value.
   */
  const handleReportUpdatedEvent = (msg: any) => {
    setState((prevState) => {
      const setStateObject = {};
      if (!msg.status) {
        return setStateObject;
      }
      const clonedInboxes = _.cloneDeep(prevState.inboxes);
      /**
       * The cloned inbox row targeted by the report-updated event, matched on id and on a non-nil isNonPhi flag equal on both sides. Undefined when no listed report satisfies both the id and the PHI-mode match.
       *
       * @returns The matching inbox row from the cloned list, or undefined.
       */
      const targetReport = _.find(
        clonedInboxes,
        (x) =>
          x.id === msg.id &&
          !_.isNil(x.isNonPhi) &&
          !_.isNil(msg.isNonPhi) &&
          x.isNonPhi === msg.isNonPhi,
      );
      if (targetReport) {
        const previousStatus = targetReport.status;
        targetReport.status = msg.status;
        let shouldReloadReportBackground = false;
        if (
          'isRenderingBackground' in msg &&
          msg.isRenderingBackground !== targetReport.isRenderingBackground
        ) {
          targetReport.isRenderingBackground = msg.isRenderingBackground;
          shouldReloadReportBackground = msg.isRenderingBackground === false;
        }
        const shouldLoadNewReport =
          (msg.status === 'Ready' && previousStatus !== 'Ready') || shouldReloadReportBackground;
        if (shouldLoadNewReport) {
          _.assign(targetReport, { pdfArrayBuffer: undefined, url: undefined });
        }
        _.assign(setStateObject, { inboxes: clonedInboxes });
      }
      return setStateObject;
    });
  };

  /**
   * Applies a mark-status-report socket message to this inbox tab, refetching when the new status falls outside the active filter. Updates the matching row's statusReport and lastStatusReportAt only when the message is newer and its status is among state.statusReports; otherwise it refetches while the tab is active, or defers the reload through needFetchDataWithStatusReportChanged.
   *
   * @param msg - The mark-status-report socket message.
   * @returns No return value.
   */
  const handleMarkStatusReportEvent = (msg: any) => {
    setState((prevState) => {
      const setStateObject = {};

      const clonedInboxes = _.cloneDeep(prevState.inboxes);
      /**
       * The cloned inbox entry whose id matches the incoming mark-status-report event, or undefined when that report is not currently listed. Derived by scanning the deep-cloned previous-state inboxes for an id equal to the message id.
       *
       * @returns The matching inbox row from the cloned list, or undefined.
       */
      const ele = _.find(clonedInboxes, (x) => x.id === msg.id);
      if (ele) {
        if (_.includes(state.statusReports, msg.statusReport)) {
          const shouldUpdate = ele.lastStatusReportAt
            ? dayjs(msg.lastStatusReportAt).isAfter(dayjs(ele.lastStatusReportAt))
            : true;
          if (shouldUpdate) {
            ele.statusReport = msg.statusReport;
            ele.lastStatusReportAt = msg.lastStatusReportAt;
            _.assign(setStateObject, { inboxes: clonedInboxes });
          }
        } else if (isActive) {
          fetchData();
        } else {
          needFetchDataWithStatusReportChanged.current = true;
        }
      }

      return setStateObject;
    });
  };

  /**
   * Keeps a click on an in-row control from reaching the surrounding inbox row. Calls stopPropagation on the received event and does nothing else.
   *
   * @param e - The click event to stop propagating.
   * @returns No return value.
   */
  const onClickStopProg = (e: any) => {
    e.stopPropagation();
  };

  /**
   * Saves a new status report for one inbox row and reconciles the optimistic update. Awaits handleMarkStatusReport, restores the pending record's prevStatus when the save was not successful, clears isSavingStatus, removes the row from listChangingStatusReport and refetches; a thrown error logs, toasts and only clears isSavingStatus.
   *
   * @param id - Id of the report whose status is saved.
   * @param statusReport - The status value to persist.
   * @returns A promise settling once state reflects the saved or reverted status.
   */
  const handleUpdateStatusReport = async (id: any, statusReport: any) => {
    try {
      const result = await handleMarkStatusReport({
        id,
        statusReport,
      });
      setState((prevState) => {
        const clonedListChangingStatusReport = [...prevState.listChangingStatusReport];
        const clonedInboxes = [...prevState.inboxes];
        /**
         * The pending status-change record for this report, looked up by id in the copied listChangingStatusReport array. Carries the prevStatus used to restore the row when the mutation fails.
         *
         * @returns The pending status-change entry for the report, or undefined.
         */
        const reportUpdating = _.find(clonedListChangingStatusReport, (item) => item.id === id);
        /**
         * The inbox row whose status change has just been persisted, looked up by id in the copied inboxes list. Used to roll the row's statusReport back and to clear its saving flag.
         *
         * @returns The matching inbox row from the copied list, or undefined.
         */
        const reportUpdated = _.find(clonedInboxes, (item) => item.id === id);
        if (!result.isSuccess) {
          reportUpdated.statusReport = reportUpdating.prevStatus;
        }
        reportUpdated.isSavingStatus = false;
        _.remove(clonedListChangingStatusReport, (item) => item.id === id);
        // *: TO DO: Optimize to next page with page size always 10
        // if (!_.includes(gotoRedux.inboxes.search.statusReports, reportUpdated.statusReport)) {
        //   _.remove(clonedInboxes, (item) => item.id === id);
        // }
        fetchData();
        return {
          listChangingStatusReport: clonedListChangingStatusReport,
          inboxes: clonedInboxes,
        };
      });
    } catch (error) {
      console.error('Failed to update report status: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState((prevState) => {
        const clonedListChangingStatusReport = [...prevState.listChangingStatusReport];
        const clonedInboxes = [...prevState.inboxes];
        /**
         * The inbox row whose status update failed, looked up by id in the copied inboxes list. Used in the error path to clear the row's isSavingStatus flag.
         *
         * @returns The matching inbox row from the copied list, or undefined.
         */
        const reportUpdated = _.find(clonedInboxes, (item) => item.id === id);
        reportUpdated.isSavingStatus = false;
        return {
          listChangingStatusReport: clonedListChangingStatusReport,
          inboxes: clonedInboxes,
        };
      });
    }
  };

  /**
   * Optimistically applies a status-report selection to one inbox row and starts the save. Inside a state updater it pushes the id, new status and previous status onto listChangingStatusReport and marks the row's statusReport and isSavingStatus, then delegates to the save that reverts the row when it fails.
   *
   * @param value - The newly selected status report.
   * @param id - Id of the inbox row being changed.
   * @returns No return value.
   */
  const onChange = (value: any, id: any) => {
    setState((prevState) => {
      const clonedListChangingStatusReport = [...prevState.listChangingStatusReport];
      const clonedInboxes = [...prevState.inboxes];
      /**
       * The inbox row the user just changed the status of, looked up by id in the copied inboxes list. Mutated with the newly selected status and flagged as saving.
       *
       * @returns The matching inbox row from the copied list, or undefined.
       */
      const reportUpdated = _.find(clonedInboxes, (item) => item.id === id);
      /**
       * The report's status value before the user's selection, read from the matching row in the copied inboxes list. Stored in the pending status-change record so the row can be reverted if the mutation fails.
       *
       * @returns The previous statusReport value, or undefined when the row is absent.
       */
      const prevStatus = _.find(clonedInboxes, (item) => item.id === id)?.statusReport;
      clonedListChangingStatusReport.push({
        id,
        status: value,
        prevStatus,
        isChangingStatus: true,
      });
      reportUpdated.statusReport = value;
      reportUpdated.isSavingStatus = true;
      return {
        listChangingStatusReport: clonedListChangingStatusReport,
        inboxes: clonedInboxes,
      };
    });
    handleUpdateStatusReport(id, value);
  };

  useEffect(() => {
    if (
      isSearchApply.current ||
      needFetchDataWithNewFacility.current ||
      needFetchDataWithStatusReportChanged.current
    ) {
      isSearchApply.current = false;
      needFetchDataWithNewFacility.current = false;
      needFetchDataWithStatusReportChanged.current = false;
      // *: InboxSearchComponent called API don't need call here
      setState({ isLoadedData: false });
      fetchData({ cursor: undefined, rankCursor: undefined });
    } else if (isActive && !state.isEndOfData) {
      setState({ isLoadedData: false });
      fetchData({ cursor: undefined, rankCursor: undefined });
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive) {
      isSearchApply.current = true;
      const {
        searchByValue,
        searchValue,
        physicianSearchValue,
        priority,
        sortValue,
        statusReports,
        statusAllReports,
      } = searchGoto;

      const dataFilter: any = {
        patientName: undefined,
        patientId: undefined,
        studyFid: undefined,
        physicianId: undefined,
        priority,
        statusReports: isAllReports ? statusAllReports : statusReports,
        sortBy: formatSortValue(sortValue ?? '').sortBy,
        sortOrder: formatSortValue(sortValue ?? '').sortOrder,
      };
      _.forEach(['patientName', 'patientId', 'studyFid', 'physicianId'], (d) => {
        if (searchByValue === d) {
          if (searchByValue === 'physicianId') {
            dataFilter.physicianId = physicianSearchValue?.value;
          } else if (searchByValue === 'studyFid') {
            dataFilter.studyFid =
              searchValue?.toString().trim() && !_.isNaN(parseInt(searchValue, 10))
                ? parseInt(searchValue, 10)
                : undefined;
          } else {
            dataFilter[d] = _.trim(searchValue);
          }
        }
      });
      filterObject.current = _.assign({ ...filterObject.current }, dataFilter);
    }
  }, [searchGoto]);

  useEffect(() => {
    /**
     * Subscription to the INBOX_EVENTS emitter channel, held so it can be removed on cleanup. Each payload is forwarded to the handler that removes recalled reports, patches listed rows, or refetches when a new report belongs to this tab.
     *
     * @returns The emitter subscription exposing remove().
     */
    const inboxEventListener = emitter.addListener(EMITTER_CONSTANTS.INBOX_EVENTS, (msg: any) => {
      handleUpdateInboxEvent(msg);
    });
    return () => {
      inboxEventListener.remove();
    };
  }, [state.inboxes]);

  useEffect(() => {
    /**
     * Subscription to the MARK_INBOX_EVENT emitter channel, held so it can be removed on cleanup. Each payload is forwarded to the handler that merges the mark data into the matching inbox row.
     *
     * @returns The emitter subscription exposing remove().
     */
    const markInboxListener = emitter.addListener(
      EMITTER_CONSTANTS.MARK_INBOX_EVENT,
      (msg: any) => {
        handleMarkInboxEvent(msg);
      },
    );

    /**
     * Subscription to the MARK_STATUS_REPORT emitter channel, held so it can be removed on cleanup. Each payload is forwarded to the handler that patches the row's status report or schedules a refetch when the status leaves the active filter.
     *
     * @returns The emitter subscription exposing remove().
     */
    const markStatusReportEventListener = emitter.addListener(
      EMITTER_CONSTANTS.MARK_STATUS_REPORT,
      (msg: any) => {
        handleMarkStatusReportEvent(msg);
      },
    );

    /**
     * Subscription to the REPORT_UPDATED emitter channel, held so it can be removed on cleanup. Each payload is forwarded to the handler that applies the new report status and clears the row's cached PDF when a new render is ready.
     *
     * @returns The emitter subscription exposing remove().
     */
    const reportUpdatedEventListener = emitter.addListener(
      EMITTER_CONSTANTS.REPORT_UPDATED,
      (msg: any) => {
        handleReportUpdatedEvent(msg);
      },
    );

    return () => {
      markInboxListener.remove();
      markStatusReportEventListener.remove();
      reportUpdatedEventListener.remove();
    };
  }, [state.inboxes]);

  useEffect(() => {
    setState({
      isEndOfData: state.inboxes.length < 10 || state.inboxes.length === totalInboxCount,
    });
  }, [totalInboxCount, state.inboxes]);

  useUpdateEffect(() => {
    if (!_.isEqual(selectedFacility.current, selectedFacilityProp)) {
      setState({
        isLoadedData: false,
        isShowFacilityName: selectedFacilityProp.value === GETTEXT_ALL_FACILITIES,
      });
      selectedFacility.current = _.cloneDeep(selectedFacilityProp);
      if (isActive) {
        fetchData({ cursor: undefined, rankCursor: undefined });
      } else {
        needFetchDataWithNewFacility.current = true;
      }
    }
  }, [selectedFacilityProp?.value]);

  useUpdateEffect(() => {
    handleReloadData();
  }, [selectedTypeSeePatients?.value]);

  useUpdateEffect(() => {
    if (
      state.inboxes.slice(
        state.pageIndex * sizePerPage.current,
        (state.pageIndex + 1) * sizePerPage.current,
      ).length === 0 &&
      state.pageIndex !== 0
    ) {
      setState({
        pageIndex: state.pageIndex - 1,
      });
    }
  }, [state.inboxes]);

  return {
    state,
    setState,
    navigate,
    filterObject,
    sizePerPage,
    isSearchApply,
    fetchData,
    onChangeSearchComponent,
    navigatePage,
    onClickReport,
    onClickStopProg,
    handleUpdateStatusReport,
    onChange,
  };
};

export { useInboxList };
